import { type CSSProperties, type ReactNode } from "react";
import { useLocation } from "react-router-dom";

import { NavSidebar, NAV_SIDEBAR_W_COLLAPSED, NAV_SIDEBAR_W_EXPANDED } from "./NavSidebar";

import {
  NavSidebarDouble,
  NAV_SPACE_RAIL_W,
  NAV_TOTAL_W_COLLAPSED,
  NAV_TOTAL_W_EXPANDED,
  shouldShowSecondaryNavSidebar,
} from "./NavSidebarDouble";

import { cn } from "@/lib/utils";
import { useAppContext } from "@/contexts/AppContext";
import { useAppLayout } from "@/hooks/useAppLayout";
import { useNavSidebarState } from "@/hooks/useNavSidebarState";
import { useAccountSetup } from "@/hooks/useAccountSetup";
import { GlobalSearchProvider } from "./GlobalSearchContext";
import { GlobalSearchBar } from "./GlobalSearchBar";

interface AppLayoutProps {
  children: ReactNode;
  /** Left of global search, desktop only — see GlobalSearchBar. */
  topbarBreadcrumb?: ReactNode;
  /** Reserve a viewport-sized internal canvas for editors and presentation mode. */
  viewportLocked?: boolean;
  /** Explicitly enables the cyan rail for a tenant-focused navigation surface. */
  tenantScopeRail?: boolean;
}

export function AppLayout({
  children,
  topbarBreadcrumb,
  viewportLocked = false,
  tenantScopeRail = false,
}: AppLayoutProps) {
  const location = useLocation();
  const { needsSetup } = useAccountSetup();

  const { dashboardFullscreen, dashboardFullscreenWidgetId, density } = useAppContext();
  const { mode: NavSidebarMode } = useNavSidebarState();
  const { layoutMode } = useAppLayout();

  const pageFullscreen = dashboardFullscreen && dashboardFullscreenWidgetId === null;

  const usesDoubleSidebar = layoutMode === "workspace";

  const expanded = NavSidebarMode === "expanded";

  const showSecondarySidebar =
    usesDoubleSidebar && shouldShowSecondaryNavSidebar(location.pathname);

  const sidebarWidth = usesDoubleSidebar
    ? showSecondarySidebar
      ? expanded
        ? NAV_TOTAL_W_EXPANDED
        : NAV_TOTAL_W_COLLAPSED
      : NAV_SPACE_RAIL_W
    : expanded
      ? NAV_SIDEBAR_W_EXPANDED
      : NAV_SIDEBAR_W_COLLAPSED;

  return (
    <GlobalSearchProvider enabled={!needsSetup}>
      <div
        data-density={density}
        className={cn(
          "relative w-full bg-background text-primary",
          viewportLocked ? "h-dvh overflow-hidden" : "min-h-dvh",
        )}
      >
        <a
          href="#main-content"
          onClick={() => document.getElementById("main-content")?.focus()}
          className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-90 focus:rounded-control focus:bg-surface focus:px-4 focus:py-3 focus:text-primary focus:outline-2 focus:outline-focus"
        >
          Skip to content
        </a>
        <div
          className={cn(
            "pointer-events-none fixed inset-x-0 top-0 z-50 h-1 origin-right bg-brand-cyan",
            "transition-[opacity,transform] duration-400 ease-[cubic-bezier(0.25,1,0.5,1)] motion-reduce:transition-none",
            tenantScopeRail ? "scale-x-100 opacity-100" : "scale-x-0 opacity-0",
          )}
          aria-hidden
        />
        <div className={cn("flex flex-col", viewportLocked ? "h-full min-h-0" : "min-h-dvh")}>
          <div className={cn("relative flex-1", viewportLocked && "min-h-0 overflow-hidden")}>
            {!pageFullscreen && usesDoubleSidebar ? <NavSidebarDouble /> : null}

            {!pageFullscreen && !usesDoubleSidebar ? <NavSidebar /> : null}

            <div
              className={cn(
                viewportLocked ? "h-full min-h-0 overflow-hidden" : "min-h-dvh",
                "transition-[padding-left] duration-350 ease-[cubic-bezier(0.25,1,0.5,1)] motion-reduce:transition-none",
                !pageFullscreen &&
                  (usesDoubleSidebar
                    ? "pl-0 md:pl-15 xl:pl-(--sidebar-width)"
                    : "pl-0 md:pl-(--sidebar-width)"),
              )}
              style={
                pageFullscreen
                  ? undefined
                  : ({ "--sidebar-width": `${sidebarWidth}px` } as CSSProperties)
              }
            >
              <div className={cn("flex flex-col", viewportLocked ? "h-full min-h-0" : "min-h-dvh")}>
                {!pageFullscreen ? (
                  <GlobalSearchBar breadcrumb={topbarBreadcrumb} searchEnabled={!needsSetup} />
                ) : null}
                {children}
              </div>
            </div>
          </div>
        </div>
      </div>
    </GlobalSearchProvider>
  );
}
